import {
  Box,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { addDays, format, setISOWeek, startOfISOWeek } from "date-fns";
import { Granularity, TrendPoint, formatNaira } from "services/finance";

// Weekly buckets come back as ISO weeks ("2026-W30"). Show the Monday it
// starts on, and the full Mon–Sun range in the tooltip.
const withReadableLabels = (series: TrendPoint[]) =>
  series.map((point) => {
    const match = /^(\d{4})-W(\d{1,2})$/.exec(point.bucket);
    if (!match) return { ...point, tooltipLabel: point.label };
    const start = startOfISOWeek(
      setISOWeek(new Date(Number(match[1]), 0, 4), Number(match[2])),
    );
    const end = addDays(start, 6);
    const sameMonth = start.getMonth() === end.getMonth();
    return {
      ...point,
      label: format(start, "d MMM"),
      tooltipLabel: sameMonth
        ? `${format(start, "d")} – ${format(end, "d MMM yyyy")}`
        : `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`,
    };
  });

const COLORS = {
  revenue: "#2F6B4F",
  expenses: "#D92D20",
  netProfit: "#0E86D4",
};

const compactNaira = (value: number) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}₦${+(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}₦${+(abs / 1_000).toFixed(1)}K`;
  return `${sign}₦${abs}`;
};

const TrendsChart = ({
  series,
  granularity,
  onGranularityChange,
  isLoading,
}: {
  series: TrendPoint[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  isLoading: boolean;
}) => {
  const chartData = withReadableLabels(series);
  // A line needs two points; show dots so a single day is still visible.
  const dot = chartData.length < 3 ? { r: 4 } : false;
  const hasNegative = series.some(
    (p) => p.revenue < 0 || p.expenses < 0 || p.netProfit < 0,
  );

  return (
    <Box
      bgcolor="#FFFFFF"
      border="1px solid #E4E7EC"
      borderRadius="12px"
      p="24px"
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        gap="12px"
        mb="16px"
      >
        <Box>
          <Typography fontSize={16} fontWeight={600} color="#101828">
            Revenue & Profit Trends
          </Typography>
          <Typography fontSize={13} color="#667085">
            Track revenue, expenses and profitability over time.
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularity}
          onChange={(_, v) => v && onGranularityChange(v)}
          sx={{
            bgcolor: "#F2F4F7",
            borderRadius: "8px",
            p: "3px",
            "& .MuiToggleButton-root": {
              border: "none",
              textTransform: "none",
              px: "16px",
              flex: { xs: 1, sm: "initial" },
              color: "#475467",
              borderRadius: "6px !important",
            },
            "& .Mui-selected": {
              bgcolor: "#FFFFFF !important",
              color: "#2F6B4F !important",
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
            },
          }}
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={300} />
      ) : (
        <Box height={320}>
          <ResponsiveContainer width="100%" height="100%">
            {/* Remount per granularity so the tooltip doesn't keep stale labels. */}
            <LineChart
              key={`${granularity}-${series[0]?.bucket}`}
              data={chartData}
              margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#EAECF0"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#667085", fontSize: 12 }}
                minTickGap={12}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={60}
                tick={{ fill: "#667085", fontSize: 11 }}
                tickFormatter={compactNaira}
              />
              <Tooltip
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.tooltipLabel || label
                }
                formatter={(value: number, name: string) => [
                  formatNaira(value),
                  name,
                ]}
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #E4E7EC",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              {/* Fixed order; Recharts would otherwise follow the draw order below. */}
              <Legend
                iconSize={8}
                wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                payload={[
                  { value: "Revenue", type: "circle", color: COLORS.revenue },
                  { value: "Expenses", type: "circle", color: COLORS.expenses },
                  {
                    value: "Net Profit",
                    type: "circle",
                    color: COLORS.netProfit,
                  },
                ]}
              />
              {hasNegative && <ReferenceLine y={0} stroke="#98A2B3" />}
              {/* Net profit is drawn first and dashed: when expenses are small it
                sits on top of revenue and would otherwise hide it. */}
              <Line
                type="linear"
                dataKey="netProfit"
                name="Net Profit"
                stroke={COLORS.netProfit}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={dot}
                activeDot={{ r: 4 }}
              />
              <Line
                type="linear"
                dataKey="expenses"
                name="Expenses"
                stroke={COLORS.expenses}
                strokeWidth={2}
                dot={dot}
                activeDot={{ r: 4 }}
              />
              <Line
                type="linear"
                dataKey="revenue"
                name="Revenue"
                stroke={COLORS.revenue}
                strokeWidth={2.5}
                dot={dot}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default TrendsChart;
